import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import { PayrollPendingIndicator } from "./PayrollPending";
import { BookOpenText, BarChart3, LogOut, Wallet, Hourglass, BookMarked, Settings as SettingsIcon, Menu } from "lucide-react";

const NavItem = ({ to, icon: Icon, label, testid, end, onNavigate }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testid}
    onClick={onNavigate}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-colors ${
        isActive
          ? "bg-ink text-white"
          : "text-ink hover:bg-paper-alt"
      }`
    }
  >
    <Icon size={16} strokeWidth={1.75} />
    <span>{label}</span>
  </NavLink>
);

/** The brand block: company name (falling back to the app name when no company is set) and, under it,
 *  the tagline — shown only when one is set, never a placeholder. Shared by the desktop sidebar, the
 *  mobile drawer and the mobile top bar so all three read the one white-label source. */
function BrandBlock() {
  const { company_name, app_name, tagline, logoUrl } = useBranding();
  const name = company_name || app_name;
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      {logoUrl ? (
        <img src={logoUrl} alt={name}
          className="h-9 w-9 shrink-0 rounded-md object-contain bg-white border border-line" />
      ) : (
        <div className="h-9 w-9 shrink-0 rounded-md bg-ink text-white flex items-center justify-center">
          <Wallet size={18} strokeWidth={1.75} />
        </div>
      )}
      <div className="min-w-0">
        <div className="font-heading font-bold text-lg leading-tight truncate">{name}</div>
        {tagline && <div className="label-eyebrow truncate">{tagline}</div>}
      </div>
    </div>
  );
}

// Primary navigation carries daily operations only — Cash Book, Suspense, Accounts Journal, Reports.
// Users, Categories and Persons live under Settings now (see web/src/pages/Settings.jsx), so only one
// admin-only link appears here. If a signed-in user may reach nothing under Settings (today, anyone who
// isn't an admin — every Settings sub-screen requires it), Settings itself does not appear.
function NavLinks({ isAdmin, onNavigate }) {
  return (
    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
      <NavItem to="/" end icon={BookOpenText} label="Cash Book" testid="nav-cashbook" onNavigate={onNavigate} />
      <NavItem to="/suspense" icon={Hourglass} label="Suspense" testid="nav-suspense" onNavigate={onNavigate} />
      <NavItem to="/journal" icon={BookMarked} label="Accounts Journal" testid="nav-journal" onNavigate={onNavigate} />
      <NavItem to="/reports" icon={BarChart3} label="Reports" testid="nav-reports" onNavigate={onNavigate} />
      {isAdmin && (
        <>
          <div className="label-eyebrow px-4 pt-5 pb-2">Configuration</div>
          <NavItem to="/settings" icon={SettingsIcon} label="Settings" testid="nav-settings" onNavigate={onNavigate} />
        </>
      )}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close the drawer on navigation — the nav links already close it on click (onNavigate below), this
  // also catches back/forward and any other route change.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Wave review, minor: SheetContent below carries lg:hidden, but the Radix dialog itself (and the
  // overlay it portals to <body>) does not — opening the drawer below 1024px and then crossing the
  // breakpoint (rotation, window resize) left the dialog genuinely open: a full-screen bg-black/80 overlay
  // and a modal focus trap over a now-invisible panel, reading as a hung app. Closing it on the crossing
  // is simpler and more general than trying to make the shared Sheet primitive responsive-aware.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => { if (e.matches) setDrawerOpen(false); };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const UserFooter = ({ onNavigate }) => (
    <div className="p-3 border-t border-line">
      <div className="px-3 py-2 mb-2">
        <div className="text-sm font-medium" data-testid="current-user-name">{user?.name}</div>
        <div className="label-eyebrow mt-0.5" data-testid="current-user-role">{user?.role}</div>
      </div>
      <button
        onClick={() => { onNavigate?.(); handleLogout(); }}
        data-testid="logout-btn"
        className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-ink hover:bg-paper-alt transition-colors"
      >
        <LogOut size={16} strokeWidth={1.75} />
        Logout
      </button>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-paper text-ink">
      {/* Mobile / tablet top bar — replaces the permanent sidebar below the lg breakpoint. This is the
          app's one literal "top bar", so the payroll indicator (Task 18) sits here. */}
      <header className="lg:hidden no-print flex items-center justify-between gap-3 px-4 py-3 border-b border-line bg-paper-card">
        <BrandBlock />
        <div className="flex items-center gap-2 shrink-0">
          <PayrollPendingIndicator />
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation menu"
            data-testid="open-nav-drawer"
            className="h-9 w-9 shrink-0 rounded-md border border-line flex items-center justify-center text-ink hover:bg-paper-alt transition-colors"
          >
            <Menu size={18} strokeWidth={1.75} />
          </button>
        </div>
      </header>

      {/* Mobile / tablet off-canvas drawer — opens on tap, closes on backdrop tap, Escape, or navigating */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" data-testid="nav-drawer" className="w-72 max-w-[85vw] p-0 flex flex-col lg:hidden">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">App navigation and account menu</SheetDescription>
          <div className="px-6 py-6 border-b border-line"><BrandBlock /></div>
          <NavLinks isAdmin={isAdmin} onNavigate={() => setDrawerOpen(false)} />
          <UserFooter onNavigate={() => setDrawerOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar — no separate top bar exists at this breakpoint (see Task 15's responsive shell),
          so the payroll indicator (Task 18) sits in this sidebar's own header row instead — the one place
          on a desktop screen that is always visible, the same reasoning BrandBlock is already duplicated
          here rather than living only in the mobile header. */}
      <aside className="hidden lg:flex lg:w-64 border-r border-line bg-paper-card flex-col no-print">
        <div className="px-6 py-6 border-b border-line flex items-center justify-between gap-3">
          <BrandBlock />
          <PayrollPendingIndicator />
        </div>
        <NavLinks isAdmin={isAdmin} />
        <UserFooter />
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 md:px-12 py-6 sm:py-10 animate-fade-up">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
